/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useApp } from '@/contexts/AppContext';
import { 
  Search, 
  Database,
  FolderOpen,
  Cloud,
  BookOpen,
  FileSpreadsheet,
  ChevronRight,
  Loader2,
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  Trash2,
} from 'lucide-react';

// Services
import {
  KNOWLEDGE_MINING_QUERY_KEYS,
  listKnowledgeSources,
  createKnowledgeSource,
  deleteKnowledgeSource,
  validateSourceConfig,
  createMiningJob,
  resolveFilesystemPath,
} from '@/services/skillshop';
import type { 
  KnowledgeSource, 
  SourceType,
} from '@/services/skillshop';
import type {
  FilesystemConfig,
  ConfluenceSpaceConfig,
  GcsBucketConfig,
  SharePointSiteConfig,
} from '@shared/schema/knowledgeMining';

// ============================================================================
// Source Card Component
// ============================================================================

interface SourceCardProps {
  source: KnowledgeSource;
  onTest: () => void;
  onDelete: () => void;
  onRun: () => void;
  isTesting: boolean;
  isDeleting: boolean;
}

function SourceCard({ source, onTest, onDelete, onRun, isTesting, isDeleting }: SourceCardProps) {
  const getSourceIcon = (type: string) => {
    switch (type) {
      case 'filesystem':
        return <FolderOpen className="h-5 w-5" />;
      case 'confluence':
        return <BookOpen className="h-5 w-5" />;
      case 'gcs':
        return <Cloud className="h-5 w-5" />;
      case 'sharepoint':
        return <FileSpreadsheet className="h-5 w-5" />;
      default:
        return <Database className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (enabled: boolean) => {
    if (!enabled) {
      return <Badge variant="outline" className="text-gray-600 bg-gray-100">Disabled</Badge>;
    }
    return <Badge variant="outline" className="text-green-600 bg-green-50">Enabled</Badge>;
  };

  const getSourcePath = (): string | null => {
    if (source.type === 'filesystem') {
      const config = source.config as FilesystemConfig;
      return config.basePath || null;
    }
    return null;
  };

  const getSourceDescription = () => {
    switch (source.type) {
      case 'filesystem': {
        const config = source.config as FilesystemConfig;
        return config.basePath || 'No path configured';
      }
      case 'confluence': {
        const config = source.config as ConfluenceSpaceConfig;
        return config.spaceKey ? `Space: ${config.spaceKey}` : 'No space configured';
      }
      case 'gcs': {
        const config = source.config as GcsBucketConfig;
        return config.bucketName || 'No bucket configured';
      }
      case 'sharepoint': {
        const config = source.config as SharePointSiteConfig;
        return config.siteUrl || 'No site configured';
      }
      default:
        return source.type;
    }
  };

  const filesystemPath = getSourcePath();

  return (
    <div className="p-4 border rounded-lg bg-slate-50 hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-slate-200">
              {getSourceIcon(source.type)}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium truncate">{source.name}</h3>
              <p className="text-sm text-muted-foreground truncate">
                {getSourceDescription()}
              </p>
            </div>
          </div>

          {/* Display filesystem path prominently */}
          {filesystemPath && (
            <div className="mt-2 mb-3 p-2 bg-slate-100 rounded border border-slate-200">
              <div className="flex items-center gap-2 text-sm">
                <FolderOpen className="h-4 w-4 text-slate-500 flex-shrink-0" />
                <code className="font-mono text-xs break-all">{filesystemPath}</code>
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-2 mb-3">
            {getStatusBadge(source.enabled)}
            <Badge variant="outline" className="text-xs bg-white">
              {source.type}
            </Badge>
          </div>

          {source.lastSync && (
            <p className="text-xs text-muted-foreground">
              Last sync: {new Date(source.lastSync).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-200">
        <Button
          variant="outline"
          size="sm"
          onClick={onTest}
          disabled={isTesting}
          className="bg-white"
        >
          {isTesting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle className="h-4 w-4 mr-1" />
          )}
          Test
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onRun}
          className="bg-white"
        >
          <Play className="h-4 w-4 mr-1" />
          Run
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={isDeleting}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 bg-white"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Add Source Dialog
// ============================================================================

interface AddSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function AddSourceDialog({ open, onOpenChange }: AddSourceDialogProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<SourceType>('filesystem');
  const [directory, setDirectory] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { activeProduct } = useApp();

  const resetForm = () => {
    setName('');
    setType('filesystem');
    setDirectory('');
    setSiteUrl('');
    setTestResult(null);
  };

  const createSourceMutation = useMutation({
    mutationFn: async () => {
      // Build the typed source input based on the discriminated union
      if (type === 'filesystem') {
        // Resolve relative paths to absolute using the active product's repo
        const resolved = await resolveFilesystemPath(
          directory,
          activeProduct?.repositoryUrl ?? undefined,
          activeProduct?.repoPath ?? undefined
        );
        
        return createKnowledgeSource({
          type: 'filesystem',
          name,
          config: { basePath: resolved.absolutePath },
        });
      }
      
      if (type === 'sharepoint') {
        // SharePoint sources use organization-level credentials configured in Settings
        // The API will validate that org credentials are configured
        return createKnowledgeSource({
          type: 'sharepoint',
          name,
          config: { siteUrl },
          auth: {
            tenantId: 'org-settings',
            clientId: 'org-settings',
            clientSecret: 'org-settings',
          },
        });
      }
      
      // TODO: Add support for confluence and gcs
      throw new Error(`Creating ${type} sources is not yet implemented`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_MINING_QUERY_KEYS.sources });
      onOpenChange(false);
      resetForm();
      toast({
        title: 'Source Created',
        description: 'Knowledge source has been created successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create source.',
        variant: 'destructive',
      });
    },
  });

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      if (type === 'filesystem') {
        // Resolve relative paths to absolute using the active product's repo
        const resolved = await resolveFilesystemPath(
          directory,
          activeProduct?.repositoryUrl ?? undefined,
          activeProduct?.repoPath ?? undefined
        );
        
        const result = await validateSourceConfig({ type: 'filesystem', basePath: resolved.absolutePath });
        setTestResult(result);
        
        toast({
          title: result.success ? 'Connection Successful' : 'Connection Failed',
          description: result.message,
          variant: result.success ? 'default' : 'destructive',
        });
      } else if (type === 'sharepoint') {
        // SharePoint testing uses organization-level credentials + site URL
        const result = await validateSourceConfig({
          type: 'sharepoint',
          tenantId: 'org-settings',
          clientId: 'org-settings',
          clientSecret: 'org-settings',
          siteUrl: siteUrl,
        });
        setTestResult(result);
        
        toast({
          title: result.success ? 'Connection Successful' : 'Connection Failed',
          description: result.message,
          variant: result.success ? 'default' : 'destructive',
        });
      } else {
        // TODO: Handle confluence and gcs testing
        throw new Error(`Testing ${type} sources is not yet implemented`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Connection test failed';
      setTestResult({ success: false, message });
      toast({
        title: 'Connection Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const canTest = (type === 'filesystem' && directory.trim().length > 0) ||
                  (type === 'sharepoint' && siteUrl.trim().length > 0);
  const canCreate = name.trim().length > 0 && 
                   ((type === 'filesystem' && directory.trim().length > 0) ||
                    (type === 'sharepoint' && siteUrl.trim().length > 0) ||
                    (type !== 'filesystem' && type !== 'sharepoint'));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Knowledge Source</DialogTitle>
          <DialogDescription>
            Configure a new source for knowledge mining.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Name</label>
            <Input
              placeholder="Source name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Type</label>
            <Select value={type} onValueChange={(v) => {
              setType(v as SourceType);
              setTestResult(null);
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="filesystem">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    Filesystem
                  </div>
                </SelectItem>
                <SelectItem value="confluence">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Confluence
                  </div>
                </SelectItem>
                <SelectItem value="gcs">
                  <div className="flex items-center gap-2">
                    <Cloud className="h-4 w-4" />
                    Google Cloud Storage
                  </div>
                </SelectItem>
                <SelectItem value="sharepoint">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    SharePoint
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === 'filesystem' && (
            <div>
              <label className="text-sm font-medium mb-2 block">Directory Path</label>
              <Input
                placeholder="docs (or /absolute/path)"
                value={directory}
                onChange={(e) => {
                  setDirectory(e.target.value);
                  setTestResult(null);
                }}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {activeProduct?.repositoryUrl
                  ? "Relative paths (e.g., 'docs') resolve to the product repository"
                  : "Select a product to use relative paths, or enter an absolute path"}
              </p>
            </div>
          )}

          {type === 'confluence' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              Confluence integration requires additional configuration in organization settings.
            </div>
          )}

          {type === 'gcs' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              GCS integration requires additional configuration in organization settings.
            </div>
          )}

          {type === 'sharepoint' && (
            <>
              <div>
                <label className="text-sm font-medium mb-2 block">SharePoint Site URL</label>
                <Input
                  placeholder="https://contoso.sharepoint.com/sites/engineering"
                  value={siteUrl}
                  onChange={(e) => {
                    setSiteUrl(e.target.value);
                    setTestResult(null);
                  }}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the full URL of the SharePoint site containing your documents
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <strong>Note:</strong> SharePoint uses organization-level credentials configured in Settings &gt; Integrations &gt; SharePoint.
              </div>
            </>
          )}

          {/* Connection Test Result */}
          {testResult && (
            <div className={`rounded-lg p-3 text-sm ${
              testResult.success
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {testResult.success ? (
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <div className="font-medium">
                    {testResult.success ? 'Connection Valid' : 'Connection Invalid'}
                  </div>
                  <div className="mt-1">{testResult.message}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={!canTest || isTesting}
          >
            {isTesting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Test Connection
          </Button>
          <Button
            onClick={() => createSourceMutation.mutate()}
            disabled={!canCreate || createSourceMutation.isPending}
          >
            {createSourceMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Add Source
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SourcesTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [testingSourceId, setTestingSourceId] = useState<string | null>(null);
  const [deletingSourceId, setDeletingSourceId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch sources
  const { data: sources, isLoading } = useQuery({
    queryKey: KNOWLEDGE_MINING_QUERY_KEYS.sources,
    queryFn: listKnowledgeSources,
    staleTime: 60 * 1000, // 1 minute
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (sourceId: string) => deleteKnowledgeSource(sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_MINING_QUERY_KEYS.sources });
      toast({
        title: 'Source Deleted',
        description: 'Knowledge source has been deleted.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete source.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setDeletingSourceId(null);
    },
  });

  // Create mining job mutation
  const createJobMutation = useMutation({
    mutationFn: (sourceId: string) => createMiningJob({ sourceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_MINING_QUERY_KEYS.jobs });
      toast({
        title: 'Mining Job Started',
        description: 'A new mining job has been created.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to start mining job.',
        variant: 'destructive',
      });
    },
  });

  const handleTestSource = async (source: KnowledgeSource) => {
    setTestingSourceId(source.id);
    try {
      if (source.type === 'filesystem') {
        const config = source.config as FilesystemConfig;
        if (!config.basePath) {
          throw new Error('No filesystem path configured for this source');
        }
        
        const result = await validateSourceConfig({ type: 'filesystem', basePath: config.basePath });
        
        toast({
          title: result.success ? 'Connection Successful' : 'Connection Failed',
          description: result.message,
          variant: result.success ? 'default' : 'destructive',
        });
      } else if (source.type === 'sharepoint') {
        const config = source.config as SharePointSiteConfig;
        if (!config.siteUrl) {
          throw new Error('No SharePoint site URL configured for this source');
        }
        
        const result = await validateSourceConfig({
          type: 'sharepoint',
          tenantId: 'org-settings',
          clientId: 'org-settings',
          clientSecret: 'org-settings',
          siteUrl: config.siteUrl,
        });
        
        toast({
          title: result.success ? 'Connection Successful' : 'Connection Failed',
          description: result.message,
          variant: result.success ? 'default' : 'destructive',
        });
      } else {
        // TODO: Handle confluence and gcs source testing
        throw new Error(`Testing ${source.type} sources is not yet implemented`);
      }
    } catch (error) {
      toast({
        title: 'Connection Failed',
        description: error instanceof Error ? error.message : 'Test failed',
        variant: 'destructive',
      });
    } finally {
      setTestingSourceId(null);
    }
  };

  const handleDeleteSource = (sourceId: string) => {
    setDeletingSourceId(sourceId);
    deleteMutation.mutate(sourceId);
  };

  const handleRunMining = (sourceId: string) => {
    createJobMutation.mutate(sourceId);
  };

  // Filter sources
  const filteredSources = sources?.filter(source => {
    if (searchQuery && !source.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (typeFilter !== 'all' && source.type !== typeFilter) {
      return false;
    }
    return true;
  }) ?? [];

  // Group sources by type for summary
  const sourceCounts = sources?.reduce((acc, source) => {
    acc[source.type] = (acc[source.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) ?? {};

  return (
    <div className="flex flex-col h-[calc(100vh-320px)]">
      {/* Header */}
      <div className="p-4 border-b space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Knowledge Sources</h3>
            <p className="text-sm text-muted-foreground">
              Configure sources for knowledge mining
            </p>
          </div>
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Source
          </Button>
        </div>

        {/* Summary */}
        {sources && sources.length > 0 && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Total: {sources.length}</span>
            {Object.entries(sourceCounts).map(([type, count]) => (
              <span key={type}>
                {type}: {count}
              </span>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search sources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="filesystem">Filesystem</SelectItem>
              <SelectItem value="confluence">Confluence</SelectItem>
              <SelectItem value="gcs">GCS</SelectItem>
              <SelectItem value="sharepoint">SharePoint</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sources Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredSources.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Database className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">No knowledge sources configured</p>
            <p className="text-sm mb-4">Add a source to start mining knowledge</p>
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Source
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSources.map((source) => (
              <SourceCard
                key={source.id}
                source={source}
                onTest={() => handleTestSource(source)}
                onDelete={() => handleDeleteSource(source.id)}
                onRun={() => handleRunMining(source.id)}
                isTesting={testingSourceId === source.id}
                isDeleting={deletingSourceId === source.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Source Dialog */}
      <AddSourceDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
      />
    </div>
  );
}
